import React, { useState, useEffect, useRef } from 'react';
import { cn } from '../../lib/utils';

// Helper: find the nearest transformed ancestor and return its rect
function getTransformedAncestorRect(el: Element | null) {
  let node: Element | null = el;
  while (node && node !== document.body) {
    const style = getComputedStyle(node);
    if (style.transform && style.transform !== 'none') {
      const rect = node.getBoundingClientRect();
      return { left: rect.left, top: rect.top, width: rect.width, height: rect.height };
    }
    // Also treat will-change: transform as transformed context
    if (style.willChange && style.willChange.includes('transform')) {
      const rect = node.getBoundingClientRect();
      return { left: rect.left, top: rect.top, width: rect.width, height: rect.height };
    }
    node = node.parentElement;
  }
  return { left: 0, top: 0, width: window.innerWidth, height: window.innerHeight };
}

export interface ContextMenuItem {
  id: string;
  label: string;
  icon?: React.ReactNode;
  onClick?: () => void;
  disabled?: boolean;
  separator?: boolean;
  submenu?: ContextMenuItem[];
}

interface ContextMenuProps {
  items: ContextMenuItem[];
  x: number;
  y: number;
  onClose: () => void;
  isVisible: boolean;
  submenuState?: {
    itemId: string;
    isOpen: boolean;
    x: number;
    y: number;
  } | null;
  setSubmenuState?: (state: {
    itemId: string;
    isOpen: boolean;
    x: number;
    y: number;
  } | null) => void;
}

export const ContextMenu: React.FC<ContextMenuProps> = ({
  items,
  x,
  y,
  onClose,
  isVisible,
  submenuState: propSubmenuState,
  setSubmenuState: propSetSubmenuState
}) => {
  const menuRef = useRef<HTMLDivElement>(null);
  
  // Use local submenu state if no props provided (for nested submenus)
  const [localSubmenuState, setLocalSubmenuState] = useState<{
    itemId: string;
    isOpen: boolean;
    x: number;
    y: number;
  } | null>(null);
  
  const submenuState = propSubmenuState !== undefined ? propSubmenuState : localSubmenuState;
  const setSubmenuState = propSetSubmenuState || setLocalSubmenuState;

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Node;
      // Don't close if clicking on this menu or any submenu
      if (menuRef.current && !menuRef.current.contains(target)) {
        // Check if click is on a submenu by looking for context menu elements
        const isOnContextMenu = (target as Element).closest('[data-context-menu]');
        if (!isOnContextMenu) {
          onClose();
        }
      }
    };

    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        onClose();
      }
    };

    if (isVisible) {
      document.addEventListener('mousedown', handleClickOutside);
      document.addEventListener('keydown', handleEscape);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('keydown', handleEscape);
    };
  }, [isVisible, onClose]);

  // Adjust position if menu would go off screen relative to transformed ancestor
  const adjustPosition = () => {
    if (!menuRef.current) return { x, y };

    const container = getTransformedAncestorRect(menuRef.current.parentElement as Element);
    const rect = menuRef.current.getBoundingClientRect();
    const viewportWidth = container.width;
    const viewportHeight = container.height;

    let adjustedX = x;
    let adjustedY = y;

    if (x + rect.width > viewportWidth) {
      adjustedX = Math.max(10, viewportWidth - rect.width - 10);
    }

    if (y + rect.height > viewportHeight) {
      adjustedY = Math.max(10, viewportHeight - rect.height - 10);
    }

    return { x: adjustedX, y: adjustedY };
  };

  const handleItemClick = (item: ContextMenuItem, event: React.MouseEvent) => {
    event.stopPropagation();
    console.log('Context menu item clicked:', item.id, item.label);
    
    if (item.disabled) {
      console.log('Item is disabled, skipping');
      return;
    }
    
    if (item.submenu) {
      const rect = menuRef.current?.getBoundingClientRect();
      if (rect) {
        const container = getTransformedAncestorRect(menuRef.current?.parentElement as Element);
        setSubmenuState({
          itemId: item.id,
          isOpen: true,
          x: rect.right - container.left, // position submenu relative to container
          y: event.currentTarget.getBoundingClientRect().top - container.top
        });
      }
      return;
    }
    
    console.log('Executing item onClick');
    if (item.onClick) {
      item.onClick();
    }
    console.log('Closing context menu');
    onClose();
  };

  const handleItemHover = (item: ContextMenuItem, event: React.MouseEvent) => {
    console.log('Item hovered:', item.id, 'has submenu:', !!item.submenu);
    if (item.submenu) {
      // Only open submenu if it's not already open for this item
      if (!submenuState || submenuState.itemId !== item.id) {
        const rect = menuRef.current?.getBoundingClientRect();
        if (rect) {
          const container = getTransformedAncestorRect(menuRef.current?.parentElement as Element);
          setSubmenuState({
            itemId: item.id,
            isOpen: true,
            x: rect.right - container.left,
            y: event.currentTarget.getBoundingClientRect().top - container.top
          });
        }
      }
    } else {
      // Only close submenu if no submenu exists for this item
      if (submenuState && !item.submenu) {
        setSubmenuState(null);
      }
    }
  };

  if (!isVisible) return null;

  const position = adjustPosition();

  return (
    <>
      <div
        ref={menuRef}
        data-context-menu
        className="fixed bg-popover border border-border rounded-md shadow-lg py-1 min-w-48 z-[9999] animate-in slide-in-from-top-1 duration-150"
        style={{
          left: position.x,
          top: position.y,
        }}
      >
        {items.map((item, index) => 
          item.separator ? (
            <div key={item.id} className="h-px bg-border my-1" />
          ) : (
            <div
              key={item.id}
              className={cn(
                "px-3 py-2 text-sm cursor-pointer flex items-center gap-3 transition-colors relative",
                item.disabled 
                  ? "text-muted-foreground cursor-not-allowed" 
                  : "hover:bg-muted text-foreground",
                item.submenu && "pr-8"
              )}
              onClick={(e) => {
                console.log('Direct click on item:', item.id);
                handleItemClick(item, e);
              }}
              onMouseEnter={(e) => handleItemHover(item, e)}
            >
              {item.icon && (
                <span className="flex-shrink-0 w-4 h-4 flex items-center justify-center">
                  {item.icon}
                </span>
              )}
              <span className="flex-1">{item.label}</span>
              {item.submenu && (
                <span className="absolute right-2 text-muted-foreground">▶</span>
              )}
            </div>
          )
        )}
      </div>

      {/* Submenu */}
      {submenuState?.isOpen && submenuState.itemId && (
        <ContextMenu
          items={items.find(item => item.id === submenuState.itemId)?.submenu || []}
          x={submenuState.x}
          y={submenuState.y}
          onClose={() => {
            setSubmenuState(null);
          }}
          isVisible={true}
        />
      )}
    </>
  );
};

// Hook for managing context menu state
export const useContextMenu = () => {
  const [contextMenu, setContextMenu] = useState<{
    isVisible: boolean;
    x: number;
    y: number;
    items: ContextMenuItem[];
  }>({
    isVisible: false,
    x: 0,
    y: 0,
    items: []
  });

  const [submenuState, setSubmenuState] = useState<{
    itemId: string;
    isOpen: boolean;
    x: number;
    y: number;
  } | null>(null);

  const showContextMenu = (event: React.MouseEvent, items: ContextMenuItem[]) => {
    event.preventDefault();
    event.stopPropagation();

    setSubmenuState(null);

    // Compute coordinates relative to nearest transformed ancestor (WindowContent)
    const targetEl = (event.currentTarget as Element) || (event.target as Element);
    const container = getTransformedAncestorRect(targetEl);

    setContextMenu({
      isVisible: true,
      x: event.clientX - container.left,
      y: event.clientY - container.top,
      items
    });
  };

  const hideContextMenu = () => {
    setContextMenu(prev => ({ ...prev, isVisible: false }));
    setSubmenuState(null);
  };

  return {
    contextMenu,
    showContextMenu,
    hideContextMenu,
    submenuState,
    setSubmenuState
  };
};